import { Box, Button, TextField, FormControlLabel, Checkbox, Typography, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import styles from "./addPermissionGroup.module.css";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import { useCRUD } from "../../../api/crud";
import { PERMISSION_GROUPS_URL } from "../../../api/permissions";
import { useGroupedPermissions } from "../../../api/permissions";
import { GroupedPermission, PermissionSubgroup } from "../../../types/permissions";

interface AddPermissionGroupProps {
  onSuccess?: () => void;
}

export const AddPermissionGroup = ({ onSuccess }: AddPermissionGroupProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { create, error } = useCRUD(PERMISSION_GROUPS_URL);
  const { query: queryGroupedPermissions, loading: permissionsLoading } = useGroupedPermissions();
  const [groupedPermissions, setGroupedPermissions] = useState<GroupedPermission[]>([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [name, setName] = useState("");
  const [isServiceRole, setIsServiceRole] = useState(false);

  useEffect(() => {
    const loadGroupedPermissions = async () => {
      const data = await queryGroupedPermissions();
      if (data) {
        setGroupedPermissions(data);
      }
    };
    loadGroupedPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllPermissionIdsFromGroup = (group: GroupedPermission): string[] => {
    const permissionIds: string[] = [];
    group.subgroups.forEach((subgroup) => {
      permissionIds.push(...Object.keys(subgroup.permissions));
    });
    return permissionIds;
  };

  const getAllPermissionIds = (): string[] => {
    const permissionIds: string[] = [];
    groupedPermissions.forEach((group) => {
      permissionIds.push(...getAllPermissionIdsFromGroup(group));
    });
    return permissionIds;
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = getAllPermissionIds();
      setSelectedPermissionIds(new Set(allIds));
    } else {
      setSelectedPermissionIds(new Set());
    }
  };

  const isAllSelected = () => {
    const allIds = getAllPermissionIds();
    return allIds.length > 0 && allIds.every((id) => selectedPermissionIds.has(id));
  };

  const handleGroupToggle = (group: GroupedPermission, checked: boolean) => {
    setSelectedPermissionIds((prev) => {
      const newSet = new Set(prev);
      const permissionIds = getAllPermissionIdsFromGroup(group);

      if (checked) {
        // Add all permissions from all subgroups in this group
        permissionIds.forEach((id) => newSet.add(id));
      } else {
        // Remove all permissions from all subgroups in this group
        permissionIds.forEach((id) => newSet.delete(id));
      }

      return newSet;
    });
  };

  const handleSubgroupToggle = (subgroup: PermissionSubgroup, checked: boolean) => {
    setSelectedPermissionIds((prev) => {
      const newSet = new Set(prev);
      const permissionIds = Object.keys(subgroup.permissions);

      if (checked) {
        permissionIds.forEach((id) => newSet.add(id));
      } else {
        permissionIds.forEach((id) => newSet.delete(id));
      }

      return newSet;
    });
  };

  const isGroupSelected = (group: GroupedPermission) => {
    const permissionIds = getAllPermissionIdsFromGroup(group);
    return permissionIds.length > 0 && permissionIds.every((id) => selectedPermissionIds.has(id));
  };

  const isGroupIndeterminate = (group: GroupedPermission) => {
    const permissionIds = getAllPermissionIdsFromGroup(group);
    const selectedCount = permissionIds.filter((id) => selectedPermissionIds.has(id)).length;
    return selectedCount > 0 && selectedCount < permissionIds.length;
  };

  const isSubgroupSelected = (subgroup: PermissionSubgroup) => {
    const permissionIds = Object.keys(subgroup.permissions);
    return permissionIds.length > 0 && permissionIds.every((id) => selectedPermissionIds.has(id));
  };

  const toggleGroupExpansion = (groupId: number) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar(t("Name is required"), ERROR_SNACKBAR);
      return;
    }

    const permissionIds = Array.from(selectedPermissionIds);

    const result = await create({
      name: name.trim(),
      permission_ids: permissionIds,
      is_service_role: isServiceRole,
    });

    if (result) {
      window.dispatchEvent(new Event("permissionGroupsUpdated"));
      onSuccess?.();
      enqueueSnackbar(t("Permission group created successfully"), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("Permission group creation failed"), ERROR_SNACKBAR);
    }
  };

  if (permissionsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <Typography color="text.primary">{t("Loading permissions...")}</Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.container} sx={{ color: 'text.primary' }}>
      <Box sx={{ width: "100%" }}>
        <TextField
          name="name"
          label={t("Name")}
          size="small"
          sx={{ width: "100%" }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        {error && (
          <div className={styles.errorMessage}>
            {error?.name?.[0]?.message}
          </div>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={isServiceRole}
              onChange={(e) => setIsServiceRole(e.target.checked)}
            />
          }
          label={t("Members can be assigned to jobs as technicians")}
          sx={{ display: "block", mt: 1, color: "text.primary" }}
        />

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.primary' }}>
            {t("Select Permissions")}
          </Typography>
          <Box sx={{ maxHeight: '400px', overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllSelected()}
                  indeterminate={!isAllSelected() && selectedPermissionIds.size > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
              label={<strong>{t("Select All")}</strong>}
              sx={{ display: 'block', mb: 2, pb: 1, borderBottom: 1, borderColor: 'divider', color: 'text.primary' }}
            />
            {groupedPermissions.map((group) => (
              <Box key={group.id} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isGroupSelected(group)}
                        indeterminate={isGroupIndeterminate(group)}
                        onChange={(e) => handleGroupToggle(group, e.target.checked)}
                      />
                    }
                    label={<span style={{ color: 'inherit' }}>{group.name}</span>}
                    sx={{ flex: 1, mb: 0.5, color: 'text.primary' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => toggleGroupExpansion(group.id)}
                    sx={{ ml: 0.5 }}
                  >
                    {expandedGroups.has(group.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={expandedGroups.has(group.id)}>
                  <Box sx={{ ml: 4, mt: 0.5 }}>
                    {group.subgroups.map((subgroup, index) => (
                      <FormControlLabel
                        key={index}
                        control={
                          <Checkbox
                            checked={isSubgroupSelected(subgroup)}
                            onChange={(e) => handleSubgroupToggle(subgroup, e.target.checked)}
                            size="small"
                          />
                        }
                        label={<span style={{ color: 'inherit' }}>{subgroup.name}</span>}
                        sx={{ display: 'block', mb: 0.5, color: 'text.primary' }}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Box>

        {error && (
          <div className={styles.errorMessage}>
            {error?.permission_ids?.[0]?.message}
          </div>
        )}

        <Button
          variant="contained"
          color="success"
          type="submit"
          onClick={handleSubmit}
          fullWidth
          sx={{ mt: 2 }}
        >
          {t("Create Permission Group")}
        </Button>
      </Box>
    </Box>
  );
};
